import React from 'react'

import { Pagination, ThemeProps } from '@hhgtech/hhg-components'
import { formatRecentPost } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { callApi, getApiPath } from 'api/utils'
import { Container } from 'components/atoms/container'
import { StyledContainer } from 'components/atoms/container/index.styled'
import { ArticleCardWrapper } from 'components/molecules/articleCardWrapper'
import { StyledArticleContainer } from 'components/pages/homepage/index.styled'
import { domainLocales } from 'domainLocales'
import { IDiscovers } from 'interfaces/categories.interface'
import { Article, Category } from 'interfaces/types'
import { useRouter } from 'next/router'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { deviceLayout } from 'styles/breakpoint'

const DiscoversStyled = styled(StyledContainer)`
  .discovers header {
    padding-bottom: 16px;
    border-bottom: 2px solid
      ${(props: ThemeProps) => props.theme.mbColors.ceruleanBlue};
    font-size: 16px;
  }
  .pagination {
    padding-bottom: 24px;
  }
  .discover {
    padding: 0;
    border-bottom: 1px solid
      ${(props: ThemeProps) => props.theme.mbColors.lightGray};
    ${deviceLayout.pcLayout`
        & > article[data-size='md'][data-direction='horizontal'] {
            padding: 16px;
            background: transparent;
        }
        margin: 8px 0;
        background: #FFFFFF;
        border: 1px solid #f3f3f3;
        box-sizing: border-box;
        box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.08);
        border-radius: 16px;
    `}
    ${deviceLayout.spLayout`
        .banner{
          min-height: auto;
        }
    `}
  }
  .btn-over-load {
    margin-bottom: 16px;
    color: ${(props: ThemeProps) => props.theme.mbColors.pink};
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    text-align: center;
  }

  .pagination {
    path {
      stroke: ${(props) => props.theme.mbColors.gray};
    }
    li[data-page-active='true'] {
      background: ${(props) => props.theme.mbColors.pink};
    }
  }
`

interface IPost {
  [key: number]: Article[]
}

const changeUrl = (categoryInfo: Category, pageNumber: number): void => {
  window.history.replaceState(
    window.history.state,
    'Category',
    `${categoryInfo.permalink}?page=${pageNumber}`,
  )
}

const Discovers = ({
  articles,
  categoryInfo,
  idExplainerPosts,
  isMobile,
}: IDiscovers) => {
  const containerRef = React.useRef<any>()
  const router = useRouter()
  const { locale } = router
  const { formatMessage: f } = useIntl()
  const sL = locale || 'vi-VN'

  const [post, setPost] = React.useState<IPost>({
    [articles.paging.currentPage || 1]: articles.posts,
  })
  const [page, setPage] = React.useState(articles.paging)

  const fetchMoreArticles = async (pageNumber: number) => {
    if (!!post[pageNumber]) {
      changeUrl(categoryInfo, pageNumber)
      setPage({ ...page, currentPage: pageNumber })
      containerRef.current.scrollIntoView({ behavior: 'smooth' })
      return false
    }

    const res = await callApi(
      getApiPath(PATHS.GET_ARTICLE_OF_CATEGORY_NO_EXPLAINERS, {
        categoryId: categoryInfo.termId,
        currentPage: pageNumber,
        ids: idExplainerPosts,
      }),
      'get',
    ).catch(() => null)

    if (!res) return false

    const relatedArticle = formatRecentPost(res)

    setPost({ ...post, [pageNumber]: [...relatedArticle.posts] })
    setPage({ ...relatedArticle.paging })
    changeUrl(categoryInfo, pageNumber)
    containerRef.current.scrollIntoView({ behavior: 'smooth' })
  }

  const itemsRender: Article[] = post[page.currentPage]

  return (
    <DiscoversStyled className="discovers" ref={containerRef}>
      <header>
        {f(
          { id: 'categoryPage.subCategories' },
          {
            name: categoryInfo.displayName,
          },
        )}
      </header>
      <StyledArticleContainer className="articles-container">
        {itemsRender?.map((article: Article, id: number) => (
          <Container key={article.id} className="discover">
            <ArticleCardWrapper
              article={article}
              headingType="h3"
              dataEventCategoryForTitle="Top Article Title"
              dataEventActionForTitle={`Click ${id + 1}`}
              dataEventLabelForTitle={`https://${domainLocales[sL]}${article.permalink}`}
              dataEventCategoryForBanner="Top Article Banner"
              dataEventActionForBanner={`Click ${id + 1}`}
              dataEventLabelForBanner={`https://${domainLocales[sL]}${article.permalink}`}
              dataEventCategoryForCategory="Top Category Title"
              dataEventActionForCategory={`Click ${id + 1}`}
              dataEventLabelForCategory={`https://${domainLocales[sL]}${article.category.permalink}`}
              type="author"
              size="md"
              banner={
                <img
                  loading="lazy"
                  src={
                    (isMobile
                      ? article.featureImageMobileXs ||
                        article.featureImageMobile
                      : article.featureImage) || '/images/bmrThumbnail.png'
                  }
                  alt={article.postTitle}
                />
              }
              categoryLink={article.category.permalink}
              articleLink={article.permalink}
              direction="horizontal"
              categoryName={article.category?.displayName}
              title={article.postTitle}
              text={''}
              styleLabel={{
                color: article.category.textColor,
              }}
              uploadTime={article.modifiedTime}
            />
          </Container>
        ))}
      </StyledArticleContainer>
      <Pagination
        className="pagination"
        pagesAmount={page.maxNumPages}
        currentPage={page.currentPage}
        onChange={fetchMoreArticles}
        href="?page="
      />
    </DiscoversStyled>
  )
}

export default Discovers
